import Quill from "quill"
import Delta from "quill-delta"
import attachIcon from "../../../icons/paperclip_icon"
import quillRegistry from "../../../quill_registry"

const base = {
  extensions: [],

  /* the following are mean to be overriden */
  configureModules(modules) {
    return modules
  },
  toolbarOptions() {
    return []
  },

  /* the following must not be overriden */

  getModules() {
    /** return the module config.
     * First, it calls the default or overriden version, then merge every extensions.
     * We need a multiple inheritance strategy here.
     */

    const base = this.configureModules({
      toolbar: this.toolbarOptions(),
      blotFormatter2: {},
    })

    const collectExtensions = (configType, startValue) => {
      return this.extensions.reduce((acc, extension) => {
        const extensionMethodName = `${configType}_${extension}`
        if (this[extensionMethodName]) {
          return { ...acc, ...this[extensionMethodName]() }
        }
        return acc
      }, startValue)
    }

    const modules = collectExtensions("modules", base)

    const keyboardBindings = collectExtensions("keyboard_bindings", {})

    return {
      ...modules,
      keyboard: {
        bindings: keyboardBindings,
      },
    }
  },

  mounted() {
    this.maybeSkipExtension()

    this.editor = this.el.querySelector('[role="rich-textarea-editor"]')
    this.hiddenInput = this.el.querySelector('[role="form-input-field"]')
    this.valueFormat = this.el.dataset.valueFormat

    const logTarget = this.el.dataset.logTarget

    this.variant = this.el.dataset.variant

    const config = {
      theme: "snow",
      modules: this.getModules(),
    }

    // Quill renders this through `.ql-editor.ql-blank::before`, so a collapsed
    // composer can say what it is without a separate label. Opt-in: editors that
    // set no data-placeholder behave exactly as before.
    if (this.el.dataset.placeholder) {
      config.placeholder = this.el.dataset.placeholder
    }

    this.quill = new Quill(this.editor, config)
    this.quill.root.setAttribute("aria-label", this.el.dataset.ariaLabel)

    quillRegistry.register(this.el.id, this.quill)

    requestAnimationFrame(() => {
      let toolbar = this.el.querySelector(".ql-toolbar")

      if (toolbar) {
        toolbar
          .querySelectorAll(
            'button, .ql-picker, .ql-picker-label, .ql-picker-item, .ql-picker-options, select, [role="button"]',
          )
          .forEach((element) => {
            element.setAttribute("tabindex", "-1")
          })
      }
    })

    // There does not seem to be an API to specify the content of custom toolbar buttons
    // E.g.: https://github.com/contentco/quill-emoji/blob/master/src/module-toolbar-emoji.js#L16
    // The document-wide lookup matches only the FIRST attach button on the page,
    // so a second editor renders a blank button, and the exact `[class=...]`
    // match breaks the moment Quill adds another class. Both are real defects,
    // but correcting them would change how existing editors render on any page
    // holding two of them -- so the compatibility variants keep the old lookup
    // and only the clean lane gets the scoped one.
    if (this.variant === "clean") {
      this.el.querySelectorAll(".ql-custom_attach").forEach((button) => {
        button.innerHTML = attachIcon
      })
    } else {
      const el = document.querySelector('[class="ql-custom_attach"]')
      if (el) {
        el.innerHTML = attachIcon
      }
    }

    this.quill.on("text-change", (delta, oldDelta, source) => {
      if (source == "user" || source == "api") {
        let value
        // getSemanticHTML() function is correctly used but there is a bug in Quill.JS.
        // This function returns correct HTML but it contains `&nbsp;` (Non Breaking Space) instead of spaces.
        // Until it's resolved we can use previous solution with `innerHTML`
        // Github Issue with bug: https://github.com/slab/quill/issues/4509
        // const html = this.quill.getSemanticHTML()
        const html = this.quill.root.innerHTML
        const completeDelta = cleanUpMentions(this.quill.getContents())

        if (logTarget) {
          // push events for logging purpose
          this.pushEvent("rich-textarea-change", {
            delta: completeDelta,
            html: html,
          })
        }

        if (this.valueFormat == "delta_with_html") {
          value = { delta: completeDelta, html: html }
        } else {
          value = { delta: completeDelta }
        }

        this.updateHiddenInput(value)
      }
    })

    this.updated()

    if (this.el.hasAttribute("data-auto-focus-on-load")) {
      this.quill.focus()
    }

    // handle updates to htmlBlock blots
    this.handleEvent("quill:htmlBlock:update", (params) => {
      const { dom_selector, content } = params
      const blot = findHtmlBlock(dom_selector)
      if (blot) {
        blot.update_html(content)
        const html = this.quill.root.innerHTML
        const completeDelta = cleanUpMentions(this.quill.getContents())
        const value =
          this.valueFormat == "delta_with_html"
            ? { delta: completeDelta, html }
            : { delta: completeDelta }
        this.updateHiddenInput(value)
      }
    })
    this.handleEvent("quill:htmlBlock:append", (params) => {
      const { content } = params
      let index = this.quill.getLength() - 1
      if (index === 0) {
        // Editor is empty — insert a newline first so user can type above the signature
        this.quill.insertText(0, "\n")
        index = 1
      }
      this.quill.insertEmbed(index, "htmlBlock", { html: content })
    })
    this.handleEvent("quill:htmlBlock:delete", (params) => {
      const { dom_selector } = params
      const blot = findHtmlBlock(dom_selector)
      if (blot) blot.remove()
    })
  },

  destroyed() {
    // With positioningStrategy "fixed" the mention list lives on
    // document.body, so it is not torn down with the editor's DOM — an open
    // list would outlive a closed form/modal, as would its scroll listener
    // (ENA-9777).
    this.quill?.getModule("mention")?.mentionContainer?.remove()
    if (this.repositionMentionList)
      window.removeEventListener("scroll", this.repositionMentionList, true)
    quillRegistry.unregister(this.id)
  },

  updated() {
    const valueDelta = this.getValueDelta()
    const contentDelta = this.quill.getContents()

    if (isDifferent(valueDelta, cleanUpMentions(contentDelta))) {
      this.quill.setContents(valueDelta)
    }
  },

  getValueDelta() {
    let valueDelta = { ops: [] }

    if (this.hiddenInput.value && this.hiddenInput.value != "") {
      const json = JSON.parse(this.hiddenInput.value) || {}
      valueDelta = json.delta || valueDelta
    }

    return new Delta(valueDelta)
  },

  maybeSkipExtension() {
    if (this.el.dataset.skipExtension) {
      this.extensions = this.extensions.filter(
        (e) => e != this.el.dataset.skipExtension,
      )
    }
  },

  updateHiddenInput(value) {
    this.hiddenInput.value = JSON.stringify(value)
    this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }))
  },

  async submit() {
    const content = this.quill.getContents()
    if (isEmpty(content)) {
      return
    }

    if (this.allLinksResolved) {
      this.quill.disable()
      await this.allLinksResolved()
      this.quill.enable()
    }

    this.quill.container
      .closest("form")
      .querySelector("button[type='submit']")
      .click()

    this.quill.setContents(new Delta().insert("\n"))
  },
}

function isDifferent(delta_a_, delta_b_) {
  if (isEmpty(delta_a_) && isEmpty(delta_b_)) return false

  const delta_a = normalize(delta_a_)
  const delta_b = normalize(delta_b_)

  const diff = delta_a.diff(delta_b)
  return diff.ops.length != 0
}

// quill.getContents returns structures with their own prototype, which messes the diff
function normalize(delta) {
  return new Delta(JSON.parse(JSON.stringify(delta)))
}

function isEmpty(delta) {
  return (
    delta.ops.length == 0 ||
    (delta.ops.length === 1 && delta.ops[0].insert === "\n")
  )
}

function cleanUpMentions(delta) {
  const ops = delta.map((op) => {
    if (op.insert.mention) {
      const mention = { ...op.insert.mention }
      delete mention.index
      return { insert: { mention: mention } }
    }
    return op
  })

  return new Delta(ops)
}

function findHtmlBlock(dom_selector) {
  const elems = document.querySelectorAll(dom_selector)
  // JS is so ugly sometimes
  let result = null
  elems.forEach((elem) => {
    const blot = Quill.find(elem, true)

    if (blot && blot.constructor.blotName === "htmlBlock") {
      result = blot
    }
  })
  return result
}
export default base
